import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Container, Card, Button } from "react-bootstrap";
import { BsKey } from "react-icons/bs";
import { LuGraduationCap } from "react-icons/lu";
import { BASE_URL } from "./commonComponents/ApiEndpoints";
import { useAuth } from "./AuthContext";
import { useSidebar } from "./SidebarContext";
import ProfileDropdown from "./commonComponents/ProfileDropdown";
import { useInstitute } from "./commonComponents/InstituteContext";

const GuardianChangePwd = () => {
  const { token, logout } = useAuth();
  const { isCollapsed } = useSidebar();
  const { id } = useParams();
  const location = useLocation();
  const { logo } = useInstitute();

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [marginLeft, setMarginLeft] = useState("0px");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  const guardian_id = location.state?.guardian_id || id;
  const user_id = location.state?.user_id || id;
  const email = location.state?.email || "";

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245;
        const collapsedWidth = 85;
        const gap = 1;

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`
        );
      } else {
        setMarginLeft("0px");
      }

      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isCollapsed]);

  const showMessage = (msg, type = "info") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
      setMessageType("");
    }, 5000);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleChangePassword = async () => {
    if (!newPassword.trim()) {
      toast.error("Please enter a new password");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    try {
      setChangingPassword(true);
      const payload = {
        user_id: Number(user_id),
        new_password: newPassword,
      };

      const res = await axios.post(
        `${BASE_URL}/users/change-password/by-id`,
        payload,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.status || res.data.message) {
        toast.success(res.data.message || "Password changed successfully!");
        setNewPassword("");
        showMessage("✅ Password changed successfully!", "success");
      } else {
        toast.error(res.data.message || "Failed to change password");
        showMessage("Failed to change password.", "error");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to change password. Please try again.";
      toast.error(errorMessage);
      showMessage(errorMessage, "error");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <>
      <div
        style={{
          marginLeft,
          transition: "margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          willChange: "margin-left",
          WebkitTransition: "margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          zIndex: 1,
          marginTop: "0px",
          marginRight: "0px",
          marginBottom: "0px",
          paddingLeft: isCollapsed ? "12px" : "12px",
          paddingRight: isCollapsed ? "12px" : "12px",
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? "55px" : "-12px",
            paddingLeft: "0px",
            paddingRight: "0px",
          }}
        >
          <div style={{ marginLeft: isMobile ? "-13px" : "-40px" }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: "#F3F4F6",
                paddingLeft: "28px",
                paddingRight: "28px",
                paddingTop: "12px",
                paddingBottom: "12px",
                overflow: "visible",
                borderBottom: "1px solid #D1D3D4",
                width: "100%",
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    background: "#223843",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: "#ffffff" }} />
                  )}
                </div>

                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: "-0.02em",
                    overflow: "visible",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "clamp(18px, 2.5vw, 22px)",
                    color: "#111827",
                    lineHeight: "1.2",
                    fontFamily: "Inter, system-ui, sans-serif",
                  }}
                >
                  Change Password
                </h3>
              </div>

              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <Container
          className="mt-4"
          style={{
            paddingLeft: isCollapsed ? "32px" : "40px",
            paddingRight: "32px",
            paddingTop: "32px",
            paddingBottom: "32px",
            maxWidth: "1200px",
          }}
        >
          {message && (
            <div
              className={`alert alert-${
                messageType === "success"
                  ? "success"
                  : messageType === "error"
                    ? "danger"
                    : "info"
              } alert-dismissible fade show d-flex align-items-center`}
              role="alert"
              style={{
                borderRadius: "10px",
                marginBottom: "24px",
              }}
            >
              <i
                className={`bi ${
                  messageType === "success"
                    ? "bi-check-circle-fill"
                    : messageType === "error"
                      ? "bi-exclamation-triangle-fill"
                      : "bi-info-circle-fill"
                } me-2`}
              ></i>
              <div className="flex-grow-1">{message}</div>
              <button
                type="button"
                className="btn-close"
                onClick={() => setMessage("")}
              ></button>
            </div>
          )}

          <Card
            style={{
              border: "2px solid #0EA5E9",
              borderRadius: "12px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              marginTop: "32px",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)",
                padding: "16px 24px",
                borderBottom: "1px solid #D1D3D4",
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "10px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <BsKey size={18} style={{ color: "#ffffff" }} />
                </div>
                <h5
                  style={{
                    margin: 0,
                    fontWeight: "700",
                    fontSize: "16px",
                    color: "#ffffff",
                    fontFamily: "Inter, system-ui, sans-serif",
                  }}
                >
                  Change Password
                </h5>
              </div>
            </div>
            <Card.Body style={{ padding: "24px" }}>
              <div className="d-flex gap-3 align-items-start flex-wrap">
                <div className="flex-grow-1" style={{ minWidth: "250px" }}>
                  <div className="input-group position-relative">
                    <span
                      className="input-group-text bg-light"
                      style={{ border: "1px solid #D1D3D4", borderRight: "none" }}
                    >
                      <BsKey size={14} className="text-muted" />
                    </span>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={changingPassword}
                      style={{
                        border: "1px solid #D1D3D4",
                        borderRadius: "0",
                        padding: "10px 12px",
                        fontSize: "14px",
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={togglePasswordVisibility}
                      disabled={changingPassword}
                      style={{
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderLeft: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "10px 12px",
                      }}
                    >
                      {showPassword ? (
                        <FaEyeSlash fontSize={16} title="Hide" />
                      ) : (
                        <FaEye fontSize={16} title="Show" />
                      )}
                    </button>
                  </div>
                  <small className="text-muted d-block mt-2">
                    <i className="bi bi-info-circle me-1"></i>
                    Password must be at least 6 characters long
                  </small>
                </div>
                <div className="d-flex align-items-end">
                  <Button
                    onClick={handleChangePassword}
                    disabled={changingPassword || !newPassword.trim()}
                    style={{
                      backgroundColor:
                        changingPassword || !newPassword.trim()
                          ? "#9ca3af"
                          : "#0EA5E9",
                      border: "none",
                      color: "#ffffff",
                      borderRadius: "8px",
                      padding: "12px 24px",
                      fontWeight: "600",
                      fontSize: "15px",
                      fontFamily: "Inter, system-ui, sans-serif",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      transition: "all 0.2s ease",
                      minHeight: "38px",
                      whiteSpace: "nowrap",
                      minWidth: "160px",
                    }}
                    onMouseEnter={(e) => {
                      if (!changingPassword && newPassword.trim()) {
                        e.currentTarget.style.backgroundColor = "#0284C7";
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!changingPassword && newPassword.trim()) {
                        e.currentTarget.style.backgroundColor = "#0EA5E9";
                        e.currentTarget.style.transform = "translateY(0)";
                      }
                    }}
                  >
                    {changingPassword ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          style={{ width: "14px", height: "14px", borderWidth: "2px" }}
                        ></span>
                        Changing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-repeat me-2"></i>
                        Change Password
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  );
};

export default GuardianChangePwd;
